/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'urql'
import { COMM_LOG_COUNT_QUERY, COMM_LOG_LIST_QUERY } from './graphql'
import type { CommLogEntryFields } from 'app/types/comm-log'
import { usePmsCommLogLabels } from './usePmsLabels'
import { SortDirection } from 'app/generated/graphql'
import { commLogSearchForPatientIds } from 'app/lib/commLog'

export const COMM_LOG_PAGE_SIZE = 50

/** Stable identity, so the label hook does not re-run on every render. */
const EMPTY_ENTRIES: CommLogEntryFields[] = []

type LoadedPage = { hits: CommLogEntryFields[]; total: number }

/**
 * Loads a contact's PMS communication log.
 *
 * The query stays paused until the tab is actually open. The drawer's parent
 * already eagerly loads activities, messages, appointments, transactions and
 * treatment plans, and this dataset runs to well over a hundred rows per
 * contact.
 *
 * "Load older" raises the limit and refetches rather than merging pages. The
 * per-patient corpus is small enough that a refetch is cheaper than the paging
 * and dedupe machinery a merge would need. Raising the limit changes the
 * operation key, so urql hands back `undefined` until the new page lands: the
 * last page is held so the list stays put instead of blanking mid-fetch.
 */
export function useCommLog({ patientIds, active }: { patientIds: string[]; active: boolean }) {
  const [limit, setLimit] = useState(COMM_LOG_PAGE_SIZE)
  const search = commLogSearchForPatientIds(patientIds)

  useEffect(() => {
    setLimit(COMM_LOG_PAGE_SIZE)
  }, [search])

  const [{ data, fetching, error }, refetch] = useQuery({
    query: COMM_LOG_LIST_QUERY,
    variables: {
      search,
      pagination: { limit },
      sort: { field: `commDate`, direction: SortDirection.Desc },
    },
    pause: !active || !search,
  })

  // Scoped to the current search so switching contacts never shows the previous
  // patient's log while the new one loads.
  const heldPage = useRef<{ search?: string; page?: LoadedPage }>({})
  if (heldPage.current.search !== search) heldPage.current = { search }
  if (data?.commLogs) heldPage.current.page = data.commLogs

  const page = data?.commLogs ?? heldPage.current.page

  // `commType` and `userNum` are PMS foreign keys. The API resolves them one
  // code at a time (TRK-5572, TRK-5573), so the names are attached here rather
  // than selected with the list.
  const entries = usePmsCommLogLabels(page?.hits ?? EMPTY_ENTRIES)

  return {
    entries,
    loadedCount: page?.hits.length ?? 0,
    total: page?.total ?? 0,
    // A paused query and an empty result both look like "no data", so the
    // caller needs to know whether anything has actually come back yet.
    hasLoaded: !!page,
    fetching,
    error,
    hasPatientLink: !!search,
    hasMore: !!page && page.hits.length < page.total,
    loadOlder: () => setLimit((current) => current + COMM_LOG_PAGE_SIZE),
    retry: () => refetch({ requestPolicy: `network-only` }),
  }
}

/**
 * Just the count, for the tab badge. Kept separate from the list so the badge
 * can be right before anyone opens the tab without pulling the rows down.
 */
export function useCommLogCount({ patientIds, enabled }: { patientIds: string[]; enabled: boolean }) {
  const search = commLogSearchForPatientIds(patientIds)
  const [{ data }] = useQuery({
    query: COMM_LOG_COUNT_QUERY,
    variables: { search },
    pause: !enabled || !search,
  })

  return data?.commLogs.total ?? 0
}
